// PURPOSE: Build a short weekly email from authorized activity and client history.
// CONTRACT: Spec 080 B2; Toronto week, past events only, saved prose stays authoritative.
// DEPENDS: Existing composition, record history and member options endpoints.
// CEILING: No AI service, invented progress or email delivery.
import { asItems, type ApiClient } from './api';
import type { AuditEntry, Membership, WeeklyComposition } from './types';
export type WeekWindow = { since: string; until: string };
const torontoDay = (date: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Toronto', year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
export function weekWindow(now = new Date()): WeekWindow {
  const until = torontoDay(now); const monday = new Date(`${until}T12:00:00Z`);
  monday.setUTCDate(monday.getUTCDate() - (monday.getUTCDay() + 6) % 7);
  return { since: monday.toISOString().slice(0, 10), until };
}
function inWeek(value: string, window: WeekWindow) {
  const date = new Date(value); if (!Number.isFinite(date.getTime())) return false;
  const day = value.length === 10 ? value : torontoDay(date);
  return day >= window.since && day <= window.until;
}
const plain = (value: unknown) => String(value || '').replace(/[_\r\n]+/g, ' ').replace(/[.!?]+$/g, '').trim();
export function buildWeeklyDraft(composition: WeeklyComposition, history: AuditEntry[], window: WeekWindow, signature: string, selected = composition.items.map(item => item.id)) {
  const items = composition.items.filter(item => selected.includes(item.id) && inWeek(item.happened_on, window));
  const sentences: string[] = [];
  const stages = history.filter(entry => inWeek(entry.created_at || entry.timestamp || '', window) && ['change_intake', 'intake_changed', 'stage_changed', 'stage_change'].includes(entry.event || entry.action || '')).sort((a, b) => (a.created_at || a.timestamp || '').localeCompare(b.created_at || b.timestamp || ''));
  const last = stages.at(-1); const after = last?.after && !Array.isArray(last.after) ? last.after : undefined;
  if (after?.intake_stage) sentences.push(`The client moved to ${plain(after.intake_stage)}.`);
  const groups = [['meeting_held', 'We met for'], ['ticket_closed', 'We closed'], ['milestone_reached', 'We reached these waves'], ['pr_merged', 'We merged']] as const;
  for (const [kind, phrase] of groups) {
    const matching = items.filter(item => item.kind === kind);
    if (matching.length) {
      const names = matching.slice(0, 3).map(item => plain(item.label) + (item.build ? ` (${plain(item.build)})` : '')).join(', ');
      sentences.push(`${phrase} ${names}${matching.length > 3 ? ` and ${matching.length - 3} more` : ''}.`);
    }
  }
  if (!sentences.length) sentences.push('No selected activity was recorded this week.');
  else if (!items.length && sentences.length === 1) sentences.push('No meetings, waves or closed tickets were selected for this update.');
  if (!after?.intake_stage && sentences.length < 3) sentences.push(`The current stage is ${plain(composition.prefill.stage)}.`);
  if (sentences.length < 6) sentences.push(composition.prefill.next_followup_due ? `The next follow-up is scheduled for ${composition.prefill.next_followup_due}.` : 'The next follow-up date is not scheduled yet.');
  return `${sentences.join(' ')}\n\n${signature}`;
}
export async function loadWeeklyDraft(api: ApiClient, clientId: string, me: Membership) {
  const window = weekWindow();
  const [composition, client] = await Promise.all([
    api.request<WeeklyComposition>(`/clients/${encodeURIComponent(clientId)}/weekly-update/compose?since=${window.since}`),
    api.record('client', clientId),
  ]);
  const history = asItems(await api.history(client));
  let sender = me.name;
  if (client.data.assigned_ae_id && client.data.assigned_ae_id !== me.id) {
    const members = await api.request<{ items: { id: string; name: string }[] }>('/members/options?role=AE');
    sender = members.items.find(member => member.id === client.data.assigned_ae_id)?.name || me.name;
  }
  const signature = sender?.trim().split(/\s+/)[0] || 'Velox';
  return { composition: { ...composition, items: composition.items.filter(item => inWeek(item.happened_on, window)) }, history, window, signature, subject: `Weekly update: ${client.data.name} (${window.until})` };
}
export type WeeklyDraftSource = Awaited<ReturnType<typeof loadWeeklyDraft>>;
